// Copyright 2016 Sorama B.V.

import { inject } from "aurelia-framework";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { RegistrableMeasurementDevice } from "services/Hardware/Datatypes/RegistrableMeasurementDevice";
import { IMeasurementDeviceManagement } from "services/Hardware/Management/IMeasurementDeviceManagement";
import { ManagementHelperFunctions } from "services/Hardware/Management/ManagementHelperFunctions";

@inject("RemoteFrontEndManagement", "CachedFrontEndManagement")
export class MeasurementDeviceManagement implements IMeasurementDeviceManagement {
    private remoteMeasurementDeviceManagement: IMeasurementDeviceManagement;
    private cachedMeasurementDeviceManagement: IMeasurementDeviceManagement;

    constructor(
        remoteFrontEndManagement: IMeasurementDeviceManagement,
        cachedFrontEndManagement: IMeasurementDeviceManagement
    ) {
        this.remoteMeasurementDeviceManagement = remoteFrontEndManagement;
        this.cachedMeasurementDeviceManagement = cachedFrontEndManagement;
    }

    public getRegisteredMeasurementDevices(measurementDevices: MeasurementDevice[]): Promise<MeasurementDevice[]> {
        // Create a copy of the MeasurementDevices
        measurementDevices = measurementDevices.map((x) => MeasurementDevice.copy(x));

        // Check if MeasurementDevice is cached
        return this.cachedMeasurementDeviceManagement
            .getRegisteredMeasurementDevices(measurementDevices)
            .then((cachedMeasurementDevices) => {
                const availableMeasurementDevices = cachedMeasurementDevices;

                // fetch not found Devices from the database.
                const notFoundMeasurementDevices = measurementDevices.filter((x) => {
                    const cachedDevice = availableMeasurementDevices.find((y) => y.id == x.id);

                    // CaptureDevices are a special case. We still can do measurements, even when we do not known the device. If a new CaputreDevice is connected,
                    // we need to check it in the database. However, if we're offline, we need to use the information that we have. Only return the known CaputreDevices in that case.
                    if (!cachedDevice) {
                        return true; // So search for the device in the cloud
                    }

                    return false;
                });

                const allowedMeasurementDevices = this.remoteMeasurementDeviceManagement
                    .getRegisteredMeasurementDevices(notFoundMeasurementDevices)
                    .then((remoteMeasurementDevices) => {
                        // Concat arrays, with some special handling for duplicates (due to capturedevices)
                        remoteMeasurementDevices.forEach((x) => {
                            let device = availableMeasurementDevices.find((y) => y.id === x.id);
                            if (device) {
                                // Duplicate, so replace
                                device = x;
                            } else {
                                availableMeasurementDevices.push(x);
                            }
                        });

                        // Cache newly fetched devices in the background
                        for (const measurementDevice of remoteMeasurementDevices) {
                            const measurementDeviceToCache =
                                RegistrableMeasurementDevice.createRegistrableCachedDevice(measurementDevice);
                            this.cachedMeasurementDeviceManagement
                                .registerMeasurementDevice(measurementDeviceToCache)
                                .then((x) => {
                                    console.warn(
                                        "MeasurementDeviceManagement: unable to cache device in browser storage"
                                    );
                                });
                        }

                        return availableMeasurementDevices;
                    })
                    .catch((x) => {
                        // If, for any reason, the remote server is throwing an error, return cached device only.
                        console.debug(x);
                        return availableMeasurementDevices;
                    });

                // And filter only the connected capture devices
                return allowedMeasurementDevices.then((x) => {
                    // Filter not connected capture devices.
                    for (const dev of x) {
                        const discoveredMeasurementDevice = measurementDevices.find((x) => x.id === dev.id);

                        if (!discoveredMeasurementDevice) continue;

                        dev.captureDevices =
                            ManagementHelperFunctions.filterConnectedCaptureDevicesForMeasurementDevice(
                                dev,
                                discoveredMeasurementDevice.captureDevices
                            );
                    }

                    return x;
                });
            });
    }

    public registerMeasurementDevice(measurementDevice: RegistrableMeasurementDevice): Promise<MeasurementDevice> {
        // Try to register it in the database
        return this.remoteMeasurementDeviceManagement.registerMeasurementDevice(measurementDevice).then(() => {
            // Add the new item to the cache
            return this.registerMeasurementDevice(measurementDevice);
        });
    }
}
